import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Edit2, Lock, Megaphone, Plus, Sparkles, Trash2, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { KpiCard, PageContainer, SectionTitle } from '@/components/design-system';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useFormations } from '@/hooks/queries/useFormation';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { useUser } from '@/hooks/useUser';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import {
  useBoostersMarketing,
  useCampagnesBoost,
  useDeleteBooster,
  useDeleteCampagneBoost,
  useDeletePageMarketing,
  usePagesMarketing,
  useRevenusStrategix,
  useRoiFormationB2c,
  useUpsertBooster,
  useUpsertCampagneBoost,
  useUpsertPageMarketing,
  type UpsertBoosterInput,
  type UpsertCampagneBoostInput,
  type UpsertPageMarketingInput,
} from '@/hooks/queries/useMarketingB2c';
import type {
  BoosterMarketingRow,
  CampagneBoostRow,
  CampagneBoostStatut,
  BoosterType,
  PageMarketingRow,
  PlateformeMarketing,
  RevenuStrategixRow,
  RoiFormationB2cRow,
} from '@/lib/database.types';
import { formatDA, formatDateDZ } from '@/lib/format';
import { LeadsTab } from './LeadsTab';
import { PerformanceTab } from './PerformanceTab';

// Sprint 7.1 — Page principale Marketing B2C avec onglets.
// Sprint 7.2 — Onglet Leads ajouté avec workflow conversion → inscription.
// Sprint 10.0 — Onglet Performance (KPIs avancés CPL/CR/top boosters).

type Tab = 'pages' | 'boosters' | 'campagnes' | 'leads' | 'perf' | 'roi';

export function MarketingB2cPage(): JSX.Element {
  const canView = usePermission('marketing.view');
  const { data: entreprises = [] } = useEntreprises();
  const { metadata } = useUser();
  const isMarketingAdmin =
    metadata?.role === 'super_admin' ||
    metadata?.role === 'direction' ||
    metadata?.role === 'responsable_marketing';

  const eurl =
    entreprises.find((e) => /kost/i.test(e.nom ?? '') && /eurl/i.test(e.nom ?? '')) ??
    entreprises[0] ??
    null;
  const [selectedEnt, setSelectedEnt] = useState<string>('');
  const entrepriseId = selectedEnt || eurl?.id || null;

  const [tab, setTab] = useState<Tab>('pages');

  const { data: pages = [] } = usePagesMarketing(entrepriseId);
  const { data: boosters = [] } = useBoostersMarketing(entrepriseId);
  const { data: campagnes = [] } = useCampagnesBoost({ entrepriseId });
  const { data: roi = [] } = useRoiFormationB2c(entrepriseId);
  const { data: revenusStrategix = [] } = useRevenusStrategix();

  const totalRevenusStrategixYTD = useMemo(() => {
    const year = new Date().getFullYear();
    return revenusStrategix
      .filter((r) => new Date(r.mois).getFullYear() === year)
      .reduce((a, r) => a + Number(r.revenu_dzd), 0);
  }, [revenusStrategix]);
  const margeKostYTD = useMemo(
    () => roi.reduce((a, r) => a + Number(r.marge_nette_kost_dzd), 0),
    [roi],
  );

  if (!canView) return <AccessDenied feature="marketing.view" />;

  return (
    <PageContainer>
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Megaphone className="h-6 w-6 text-rose-600" /> Marketing B2C
          </h1>
          <p className="text-sm text-muted-foreground">
            Pages, boosters, campagnes Facebook/TikTok et ROI hebdomadaire.
          </p>
        </div>
        {entreprises.length > 1 && (
          <select
            value={selectedEnt}
            onChange={(e) => setSelectedEnt(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs"
          >
            <option value="">{eurl?.nom ?? 'Toutes'}</option>
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        )}
      </header>

      {!isMarketingAdmin && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Vue restreinte — vous voyez uniquement vos pages, vos leads, et les campagnes de vos
            pages. Les créations/suppressions de pages, boosters et campagnes sont réservées au
            responsable marketing.
          </span>
        </div>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Pages actives" value={String(pages.filter((p) => p.is_active).length)} />
        <KpiCard
          label="Campagnes en cours"
          value={String(campagnes.filter((c) => c.statut === 'en_cours').length)}
        />
        <KpiCard label="Marge KOST (cumul)" value={formatDA(margeKostYTD)} variant="finances" />
        <KpiCard
          label="Strategix YTD"
          value={formatDA(totalRevenusStrategixYTD)}
          variant="formation_b2c"
        />
      </div>

      <nav className="mb-4 flex flex-wrap items-center gap-1 border-b border-slate-200">
        {(
          [
            ['pages', 'Pages', pages.length],
            ['boosters', 'Boosters', boosters.length],
            ['campagnes', 'Campagnes', campagnes.length],
            ['leads', 'Leads', 0],
            ['perf', 'Performance', 0],
            ['roi', 'ROI', roi.length],
          ] as [Tab, string, number][]
        ).map(([k, l, n]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              tab === k
                ? 'border-b-2 border-rose-600 px-3 py-2 text-xs font-semibold text-rose-700'
                : 'border-b-2 border-transparent px-3 py-2 text-xs text-slate-600 hover:text-slate-900'
            }
          >
            {l} <span className="ml-1 text-[10px] text-slate-500">({n})</span>
          </button>
        ))}
      </nav>

      {tab === 'pages' && entrepriseId && (
        <PagesTab pages={pages} entrepriseId={entrepriseId} canEdit={isMarketingAdmin} />
      )}
      {tab === 'boosters' && entrepriseId && (
        <BoostersTab boosters={boosters} entrepriseId={entrepriseId} canEdit={isMarketingAdmin} />
      )}
      {tab === 'campagnes' && entrepriseId && (
        <CampagnesTab
          campagnes={campagnes}
          pages={pages}
          boosters={boosters}
          entrepriseId={entrepriseId}
          canEdit={isMarketingAdmin}
        />
      )}
      {tab === 'leads' && entrepriseId && (
        <LeadsTab entrepriseId={entrepriseId} pages={pages} campagnes={campagnes} />
      )}
      {tab === 'perf' && entrepriseId && (
        <PerformanceTab
          entrepriseId={entrepriseId}
          pages={pages}
          boosters={boosters}
          campagnes={campagnes}
        />
      )}
      {tab === 'roi' && <RoiTab roi={roi} revenusStrategix={revenusStrategix} />}
    </PageContainer>
  );
}

// ===== Pages tab =====

function PagesTab({
  pages,
  entrepriseId,
  canEdit,
}: {
  pages: PageMarketingRow[];
  entrepriseId: string;
  canEdit: boolean;
}): JSX.Element {
  const { data: users = [] } = useUsersList();
  const upsert = useUpsertPageMarketing();
  const del = useDeletePageMarketing();
  const [editing, setEditing] = useState<PageMarketingRow | null>(null);
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState<PageMarketingRow | null>(null);

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);

  return (
    <>
      <SectionTitle
        trailing={
          canEdit ? (
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Nouvelle page
            </Button>
          ) : undefined
        }
      >
        Pages Facebook/TikTok
      </SectionTitle>
      {pages.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Aucune page enregistrée. Crée la première page que tes équipes B2C utilisent pour recevoir
          les messages des prospects.
        </p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Plateforme</th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-left">URL</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {p.plateforme}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-medium">{p.nom}</td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-blue-600">
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">
                        {p.url}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {p.owner_user_id ? (userMap.get(p.owner_user_id) ?? '—') : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {p.is_active ? (
                      <Badge variant="success" className="text-[10px]">
                        Actif
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        Inactif
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditing(p);
                          setOpen(true);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-rose-600"
                        onClick={() => setConfirmDel(p)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PageMarketingFormDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setEditing(null);
          }
        }}
        editing={editing}
        entrepriseId={entrepriseId}
        users={users.map((u) => ({ id: u.user_id, name: u.full_name ?? u.user_id.slice(0, 8) }))}
        onSubmit={async (input) => {
          await upsert.mutateAsync(input);
          toast.success(editing ? 'Page mise à jour' : 'Page créée');
          setOpen(false);
          setEditing(null);
        }}
        isSubmitting={upsert.isPending}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        destructive
        title={confirmDel ? `Supprimer ${confirmDel.nom} ?` : ''}
        description="Cette action est irréversible."
        loading={del.isPending}
        onConfirm={async () => {
          if (!confirmDel) return;
          await del.mutateAsync(confirmDel.id);
          toast.success('Page supprimée');
          setConfirmDel(null);
        }}
      />
    </>
  );
}

function PageMarketingFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  users,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: PageMarketingRow | null;
  entrepriseId: string;
  users: { id: string; name: string }[];
  onSubmit: (input: UpsertPageMarketingInput) => Promise<void>;
  isSubmitting: boolean;
}): JSX.Element {
  const [plateforme, setPlateforme] = useState<PlateformeMarketing>('facebook');
  const [nom, setNom] = useState('');
  const [url, setUrl] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [isActive, setIsActive] = useState(true);

  useMemo(() => {
    if (open) {
      setPlateforme(editing?.plateforme ?? 'facebook');
      setNom(editing?.nom ?? '');
      setUrl(editing?.url ?? '');
      setOwnerId(editing?.owner_user_id ?? '');
      setIsActive(editing?.is_active ?? true);
    }
  }, [open, editing]);

  const handle = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await onSubmit({
      id: editing?.id,
      entreprise_id: entrepriseId,
      plateforme,
      nom: nom.trim(),
      url: url.trim() || null,
      owner_user_id: ownerId || null,
      is_active: isActive,
    });
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Modifier la page' : 'Nouvelle page'}
      onSubmit={handle}
      isSubmitting={isSubmitting}
      submitLabel={editing ? 'Enregistrer' : 'Créer'}
      size="md"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormSelect
          label="Plateforme"
          value={plateforme}
          onChange={(v) => setPlateforme(v as PlateformeMarketing)}
          options={[
            { value: 'facebook', label: 'Facebook' },
            { value: 'tiktok', label: 'TikTok' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'autre', label: 'Autre' },
          ]}
          required
        />
        <FormField
          label="Nom de la page"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          required
        />
        <FormField
          label="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          fullWidth
          hint="Lien public de la page (optionnel)"
        />
        <FormSelect
          label="Owner (commerciale)"
          value={ownerId}
          onChange={setOwnerId}
          options={[
            { value: '', label: '— Aucun —' },
            ...users.map((u) => ({ value: u.id, label: u.name })),
          ]}
          hint="L'utilisatrice qui répond aux messages reçus sur cette page"
        />
        <label className="flex items-center gap-2 text-xs sm:col-span-2">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <span>Page active</span>
        </label>
      </div>
    </FormDialog>
  );
}

// ===== Boosters tab =====

function BoostersTab({
  boosters,
  entrepriseId,
  canEdit,
}: {
  boosters: BoosterMarketingRow[];
  entrepriseId: string;
  canEdit: boolean;
}): JSX.Element {
  const { data: users = [] } = useUsersList();
  const upsert = useUpsertBooster();
  const del = useDeleteBooster();
  const [editing, setEditing] = useState<BoosterMarketingRow | null>(null);
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState<BoosterMarketingRow | null>(null);

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);

  return (
    <>
      <SectionTitle
        trailing={
          canEdit ? (
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Nouveau booster
            </Button>
          ) : undefined
        }
      >
        Boosters marketing
      </SectionTitle>
      {boosters.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Aucun booster. Ajoute des boosters internes (équipe interne) ou externes (avec un compte
          CCP de versement) pour leur attribuer des campagnes.
        </p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-left">User / CCP</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {boosters.map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <Badge
                      variant={b.type === 'interne' ? 'secondary' : 'outline'}
                      className="text-[10px] capitalize"
                    >
                      {b.type}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-medium">{b.nom}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">
                    {b.type === 'interne'
                      ? b.user_id
                        ? (userMap.get(b.user_id) ?? b.user_id.slice(0, 8))
                        : '—'
                      : (b.ccp_compte ?? '—')}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {[b.telephone, b.email].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {b.is_active ? (
                      <Badge variant="success" className="text-[10px]">
                        Actif
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        Inactif
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditing(b);
                          setOpen(true);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-rose-600"
                        onClick={() => setConfirmDel(b)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BoosterFormDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setEditing(null);
          }
        }}
        editing={editing}
        entrepriseId={entrepriseId}
        users={users.map((u) => ({ id: u.user_id, name: u.full_name ?? u.user_id.slice(0, 8) }))}
        onSubmit={async (input) => {
          await upsert.mutateAsync(input);
          toast.success(editing ? 'Booster mis à jour' : 'Booster créé');
          setOpen(false);
          setEditing(null);
        }}
        isSubmitting={upsert.isPending}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        destructive
        title={confirmDel ? `Supprimer ${confirmDel.nom} ?` : ''}
        description="Cette action est irréversible."
        loading={del.isPending}
        onConfirm={async () => {
          if (!confirmDel) return;
          await del.mutateAsync(confirmDel.id);
          toast.success('Booster supprimé');
          setConfirmDel(null);
        }}
      />
    </>
  );
}

function BoosterFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  users,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: BoosterMarketingRow | null;
  entrepriseId: string;
  users: { id: string; name: string }[];
  onSubmit: (input: UpsertBoosterInput) => Promise<void>;
  isSubmitting: boolean;
}): JSX.Element {
  const [type, setType] = useState<BoosterType>('externe');
  const [nom, setNom] = useState('');
  const [userId, setUserId] = useState('');
  const [ccp, setCcp] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useMemo(() => {
    if (open) {
      setType(editing?.type ?? 'externe');
      setNom(editing?.nom ?? '');
      setUserId(editing?.user_id ?? '');
      setCcp(editing?.ccp_compte ?? '');
      setTelephone(editing?.telephone ?? '');
      setEmail(editing?.email ?? '');
      setNotes(editing?.notes ?? '');
      setIsActive(editing?.is_active ?? true);
      setError(null);
    }
  }, [open, editing]);

  const handle = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (type === 'interne' && !userId) return setError('Booster interne : utilisateur obligatoire');
    if (type === 'externe' && !ccp.trim())
      return setError('Booster externe : compte CCP obligatoire');
    try {
      await onSubmit({
        id: editing?.id,
        entreprise_id: entrepriseId,
        nom: nom.trim(),
        type,
        user_id: type === 'interne' ? userId : null,
        ccp_compte: type === 'externe' ? ccp.trim() : null,
        telephone: telephone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
        is_active: isActive,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Modifier le booster' : 'Nouveau booster'}
      onSubmit={handle}
      isSubmitting={isSubmitting}
      submitLabel={editing ? 'Enregistrer' : 'Créer'}
      size="md"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormSelect
          label="Type"
          value={type}
          onChange={(v) => setType(v as BoosterType)}
          options={[
            { value: 'externe', label: 'Externe (CCP)' },
            { value: 'interne', label: 'Interne (équipe)' },
          ]}
          required
        />
        <FormField
          label="Nom"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          required
          hint="Surnom de test, peu importe — X1, X2..."
        />
        {type === 'interne' ? (
          <FormSelect
            label="Utilisateur interne"
            value={userId}
            onChange={setUserId}
            options={[
              { value: '', label: '— Sélectionner —' },
              ...users.map((u) => ({ value: u.id, label: u.name })),
            ]}
            required
          />
        ) : (
          <FormField
            label="Compte CCP"
            value={ccp}
            onChange={(e) => setCcp(e.target.value)}
            required
            hint="N° CCP où virer le budget boost"
          />
        )}
        <FormField
          label="Téléphone"
          value={telephone}
          onChange={(e) => setTelephone(e.target.value)}
        />
        <FormField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FormTextarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          fullWidth
        />
        <label className="flex items-center gap-2 text-xs sm:col-span-2">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <span>Booster actif</span>
        </label>
      </div>
    </FormDialog>
  );
}

// ===== Campagnes tab =====

function CampagnesTab({
  campagnes,
  pages,
  boosters,
  entrepriseId,
  canEdit,
}: {
  campagnes: CampagneBoostRow[];
  pages: PageMarketingRow[];
  boosters: BoosterMarketingRow[];
  entrepriseId: string;
  canEdit: boolean;
}): JSX.Element {
  const { data: formations = [] } = useFormations({ entrepriseId });
  const upsert = useUpsertCampagneBoost();
  const del = useDeleteCampagneBoost();
  const [editing, setEditing] = useState<CampagneBoostRow | null>(null);
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState<CampagneBoostRow | null>(null);

  const formationMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of formations) m.set(f.id, `${f.code} · ${f.titre}`);
    return m;
  }, [formations]);
  const pageMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pages) m.set(p.id, `${p.plateforme} · ${p.nom}`);
    return m;
  }, [pages]);
  const boosterMap = useMemo(() => {
    const m = new Map<string, BoosterMarketingRow>();
    for (const b of boosters) m.set(b.id, b);
    return m;
  }, [boosters]);

  return (
    <>
      <SectionTitle
        trailing={
          canEdit ? (
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
              disabled={formations.length === 0 || boosters.length === 0}
            >
              <Plus className="h-3.5 w-3.5" /> Nouvelle campagne
            </Button>
          ) : undefined
        }
      >
        Campagnes boost FB/TikTok
      </SectionTitle>
      {campagnes.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          {formations.length === 0
            ? 'Crée au moins une formation B2C dans le module Formation avant de lancer une campagne.'
            : boosters.length === 0
              ? 'Crée au moins un booster (onglet précédent) avant de lancer une campagne.'
              : 'Aucune campagne. Crée la première : 1 boost = 1 formation × 1 booster × 1 budget.'}
        </p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Réf</th>
                <th className="px-3 py-2 text-left">Formation</th>
                <th className="px-3 py-2 text-left">Page</th>
                <th className="px-3 py-2 text-left">Booster</th>
                <th className="px-3 py-2 text-right">Budget</th>
                <th className="px-3 py-2">Période</th>
                <th className="px-3 py-2 text-right">Inscrits</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {campagnes.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-[10px]">{c.reference}</td>
                  <td className="truncate px-3 py-2">{formationMap.get(c.formation_id) ?? '—'}</td>
                  <td className="truncate px-3 py-2">
                    {c.page_id ? (pageMap.get(c.page_id) ?? '—') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {c.booster_id ? (boosterMap.get(c.booster_id)?.nom ?? '—') : '—'}
                  </td>
                  <td className="num px-3 py-2 text-right font-semibold">
                    {formatDA(Number(c.montant))}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">
                    {formatDateDZ(c.date_debut)}
                    {c.date_fin && <> → {formatDateDZ(c.date_fin)}</>}
                  </td>
                  <td className="num px-3 py-2 text-right">
                    {c.nb_inscrits}{' '}
                    <span className="text-[10px] text-slate-500">/ {c.nb_messages_recus} msg</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <StatutBadge statut={c.statut} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditing(c);
                          setOpen(true);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-rose-600"
                        onClick={() => setConfirmDel(c)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CampagneBoostFormDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setEditing(null);
          }
        }}
        editing={editing}
        entrepriseId={entrepriseId}
        formations={formations.map((f) => ({ id: f.id, label: `${f.code} · ${f.titre}` }))}
        pages={pages.map((p) => ({ id: p.id, label: `${p.plateforme} · ${p.nom}` }))}
        boosters={boosters.map((b) => ({ id: b.id, label: `${b.nom} (${b.type})` }))}
        onSubmit={async (input) => {
          await upsert.mutateAsync(input);
          toast.success(editing ? 'Campagne mise à jour' : 'Campagne créée');
          setOpen(false);
          setEditing(null);
        }}
        isSubmitting={upsert.isPending}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        destructive
        title={confirmDel ? `Supprimer ${confirmDel.reference} ?` : ''}
        description="Cette action est irréversible."
        loading={del.isPending}
        onConfirm={async () => {
          if (!confirmDel) return;
          await del.mutateAsync(confirmDel.id);
          toast.success('Campagne supprimée');
          setConfirmDel(null);
        }}
      />
    </>
  );
}

function StatutBadge({ statut }: { statut: CampagneBoostStatut }): JSX.Element {
  const v = {
    planifiee: 'outline',
    en_cours: 'secondary',
    terminee: 'success',
    annulee: 'destructive',
  } as const;
  return (
    <Badge variant={v[statut]} className="text-[10px] capitalize">
      {statut.replace('_', ' ')}
    </Badge>
  );
}

function CampagneBoostFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  formations,
  pages,
  boosters,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: CampagneBoostRow | null;
  entrepriseId: string;
  formations: { id: string; label: string }[];
  pages: { id: string; label: string }[];
  boosters: { id: string; label: string }[];
  onSubmit: (input: UpsertCampagneBoostInput) => Promise<void>;
  isSubmitting: boolean;
}): JSX.Element {
  const today = new Date().toISOString().slice(0, 10);
  const [formationId, setFormationId] = useState('');
  const [pageId, setPageId] = useState('');
  const [boosterId, setBoosterId] = useState('');
  const [montant, setMontant] = useState('10000');
  const [dateDebut, setDateDebut] = useState(today);
  const [dateFin, setDateFin] = useState('');
  const [statut, setStatut] = useState<CampagneBoostStatut>('planifiee');
  const [nbMessages, setNbMessages] = useState('0');
  const [nbInscrits, setNbInscrits] = useState('0');
  const [notes, setNotes] = useState('');

  useMemo(() => {
    if (open) {
      setFormationId(editing?.formation_id ?? '');
      setPageId(editing?.page_id ?? '');
      setBoosterId(editing?.booster_id ?? '');
      setMontant(String(editing?.montant ?? 10000));
      setDateDebut(editing?.date_debut ?? today);
      setDateFin(editing?.date_fin ?? '');
      setStatut(editing?.statut ?? 'planifiee');
      setNbMessages(String(editing?.nb_messages_recus ?? 0));
      setNbInscrits(String(editing?.nb_inscrits ?? 0));
      setNotes(editing?.notes ?? '');
    }
  }, [open, editing, today]);

  const handle = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await onSubmit({
      id: editing?.id,
      entreprise_id: entrepriseId,
      formation_id: formationId,
      page_id: pageId || null,
      booster_id: boosterId || null,
      montant: Number(montant),
      date_debut: dateDebut,
      date_fin: dateFin || null,
      statut,
      nb_messages_recus: Number(nbMessages),
      nb_inscrits: Number(nbInscrits),
      notes: notes.trim() || null,
    });
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Modifier la campagne' : 'Nouvelle campagne boost'}
      onSubmit={handle}
      isSubmitting={isSubmitting}
      submitLabel={editing ? 'Enregistrer' : 'Créer'}
      size="lg"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormSelect
          label="Formation cible"
          value={formationId}
          onChange={setFormationId}
          options={[
            { value: '', label: '— Sélectionner —' },
            ...formations.map((f) => ({ value: f.id, label: f.label })),
          ]}
          required
        />
        <FormSelect
          label="Page"
          value={pageId}
          onChange={setPageId}
          options={[
            { value: '', label: '— Aucune —' },
            ...pages.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        <FormSelect
          label="Booster"
          value={boosterId}
          onChange={setBoosterId}
          options={[
            { value: '', label: '— Aucun —' },
            ...boosters.map((b) => ({ value: b.id, label: b.label })),
          ]}
        />
        <FormField
          label="Budget (DA)"
          type="number"
          step="100"
          min="0"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
          required
        />
        <FormField
          label="Date début"
          type="date"
          value={dateDebut}
          onChange={(e) => setDateDebut(e.target.value)}
          required
        />
        <FormField
          label="Date fin"
          type="date"
          value={dateFin}
          onChange={(e) => setDateFin(e.target.value)}
        />
        <FormSelect
          label="Statut"
          value={statut}
          onChange={(v) => setStatut(v as CampagneBoostStatut)}
          options={[
            { value: 'planifiee', label: 'Planifiée' },
            { value: 'en_cours', label: 'En cours' },
            { value: 'terminee', label: 'Terminée' },
            { value: 'annulee', label: 'Annulée' },
          ]}
        />
        <FormField
          label="Messages reçus"
          type="number"
          step="1"
          min="0"
          value={nbMessages}
          onChange={(e) => setNbMessages(e.target.value)}
          hint="Mettre à jour en fin de campagne"
        />
        <FormField
          label="Inscrits convertis"
          type="number"
          step="1"
          min="0"
          value={nbInscrits}
          onChange={(e) => setNbInscrits(e.target.value)}
        />
        <FormTextarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          fullWidth
        />
      </div>
    </FormDialog>
  );
}

// ===== ROI tab =====

function RoiTab({
  roi,
  revenusStrategix,
}: {
  roi: RoiFormationB2cRow[];
  revenusStrategix: RevenuStrategixRow[];
}): JSX.Element {
  return (
    <div className="space-y-6">
      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" /> ROI par formation B2C
          </span>
        </SectionTitle>
        {roi.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Pas encore de données. Crée une formation, une campagne, des inscriptions, et le ROI
            apparaîtra ici (recettes − Strategix − primes − boost = marge KOST).
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Formation</th>
                  <th className="px-3 py-2 text-right">Inscrits</th>
                  <th className="px-3 py-2 text-right">Recettes</th>
                  <th className="px-3 py-2 text-right">Strategix</th>
                  <th className="px-3 py-2 text-right">Primes</th>
                  <th className="px-3 py-2 text-right">Boost</th>
                  <th className="px-3 py-2 text-right">Formateur</th>
                  <th className="px-3 py-2 text-right">Matériel</th>
                  <th className="px-3 py-2 text-right">Marge nette KOST</th>
                </tr>
              </thead>
              <tbody>
                {roi.map((r) => (
                  <tr key={r.formation_id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <span className="font-mono text-[10px] text-slate-500">{r.code}</span>{' '}
                      <span className="font-medium">{r.titre}</span>
                    </td>
                    <td className="num px-3 py-2 text-right">{r.nb_inscriptions}</td>
                    <td className="num px-3 py-2 text-right">{formatDA(Number(r.recettes_dzd))}</td>
                    <td className="num px-3 py-2 text-right text-rose-700">
                      −{formatDA(Number(r.cout_strategix_dzd))}
                    </td>
                    <td className="num px-3 py-2 text-right text-rose-700">
                      −{formatDA(Number(r.cout_primes_dzd))}
                    </td>
                    <td className="num px-3 py-2 text-right text-rose-700">
                      −{formatDA(Number(r.cout_boost_dzd))}
                    </td>
                    <td className="num px-3 py-2 text-right text-rose-700">
                      −{formatDA(Number(r.cout_formateur_dzd))}
                    </td>
                    <td className="num px-3 py-2 text-right text-rose-700">
                      −{formatDA(Number(r.cout_materiel_dzd))}
                    </td>
                    <td
                      className={`num px-3 py-2 text-right font-bold ${
                        Number(r.marge_nette_kost_dzd) >= 0 ? 'text-emerald-700' : 'text-rose-700'
                      }`}
                    >
                      {formatDA(Number(r.marge_nette_kost_dzd))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground">
          Marge nette = recettes − Strategix − Primes − Boost − Formateur − Matériel. Coût matériel
          saisi sur chaque session (sessions_formation.cout_materiel_dzd) ; coût formateur agrégé
          via paiements_formateurs.
        </p>
      </section>

      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" /> Revenus Strategix (poche perso)
          </span>
        </SectionTitle>
        {revenusStrategix.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Pas encore de revenus.</p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-xl border border-violet-200 bg-violet-50/30">
            <table className="w-full text-xs">
              <thead className="bg-violet-100 text-[10px] font-bold uppercase text-violet-800">
                <tr>
                  <th className="px-3 py-2 text-left">Mois</th>
                  <th className="px-3 py-2 text-right">Nb certificats</th>
                  <th className="px-3 py-2 text-right">Revenu DZD</th>
                </tr>
              </thead>
              <tbody>
                {revenusStrategix.map((r) => (
                  <tr key={r.mois} className="border-t border-violet-200/50">
                    <td className="px-3 py-2 font-mono">
                      {new Date(r.mois).toLocaleDateString('fr-DZ', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="num px-3 py-2 text-right">{r.nb_certifs}</td>
                    <td className="num px-3 py-2 text-right font-bold text-violet-900">
                      {formatDA(Number(r.revenu_dzd))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
